import cn from 'classnames';
import { usePathname, useSearchParams } from 'next/navigation';
import { useState, useEffect } from 'react';

import { useDictionary } from 'src/hooks/useDictionary';
import { useOnMount } from 'src/hooks/useOnMount';
import { useRouteContext } from 'src/hooks/useRouteContext';

import { getActiveJumpLink, getAllJumpLinks, JumpLinkData } from 'src/utils/jumpLinks';

import { ComponentData, CorePageJumpLinkNavigationData } from 'src/types/components';

import CaretUp from 'assets/icons/caret-up.svg';

import StartIndex from 'components/BaseHelpers/StartIndex';
import StopIndex from 'components/BaseHelpers/StopIndex';
import Text from 'components/BaseHelpers/Text';

import { ComponentProps } from '../../../types/component-props';

import styles from './CorePageJumpLinkNavigation.module.scss';

export type CorePageJumpLinkNavigationProps = ComponentProps & CorePageJumpLinkNavigationData;

const CorePageJumpLinkNavigation = (props: CorePageJumpLinkNavigationProps): JSX.Element => {
  const { header } = props.fields;
  const [renderedJumpLinks, setRenderedJumpLinks] = useState<Array<JumpLinkData>>([]);
  const { jumpLinksDropdownLabel } = useDictionary();
  const routeContext = useRouteContext();
  const [activeJumpLink, setActiveJumpLink] = useState<JumpLinkData['element'] | null>(null);

  const pageDataHasJumpLinks = () => {
    return routeContext.routeData?.route.placeholders['content'].some((component) => {
      type MaybeHasJumpLinkLabel = { fields?: { jumpLinkLabel?: string } };

      const hasJumpLinkLabel = (component: ComponentData) => {
        if (component.componentName === 'CoreLocationsListingGroups') {
          return component.fields.groups.some((group) =>
            group.items.some((item) => Boolean(item.name))
          );
        }
        return (component as MaybeHasJumpLinkLabel).fields?.jumpLinkLabel;
      };

      if (hasJumpLinkLabel(component)) {
        return true;
      } else if (component.componentName === 'CoreTwoColumnLayout') {
        return component.placeholders.rightColumn.some(hasJumpLinkLabel);
      } else {
        return false;
      }
    });
  };

  const { isMounted } = useOnMount(() => setRenderedJumpLinks(getAllJumpLinks()));

  const pathname = usePathname();
  const searchParams = useSearchParams();
  useEffect(() => setRenderedJumpLinks(getAllJumpLinks()), [pathname, searchParams]);

  useEffect(() => {
    const handleScroll = () => {
      const activeElement = getActiveJumpLink()?.element || null;
      if (activeElement !== activeJumpLink) {
        setActiveJumpLink(activeElement);
      }
    };
    handleScroll(); // Set initial active jump link on mount
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  if (!pageDataHasJumpLinks() || (isMounted && renderedJumpLinks.length === 0)) {
    return <></>;
  }

  return (
    <>
      <StopIndex />
      <div className={styles.main} data-component="CorePageJumpLinkNavigation">
        <div className={styles.desktop}>
          {header && <Text className={cn(styles.header, 'body-eyebrow')} field={header} />}
          <ul className={styles.list}>
            {renderedJumpLinks.map((jumpLink, index) => (
              <li key={`link-${index}`} className={styles['list-item']}>
                <button
                  onClick={() =>
                    jumpLink.element.scrollIntoView({ behavior: 'smooth', block: 'start' })
                  }
                  className={cn(styles.button, 'body-med', {
                    [styles.active]: jumpLink.element === activeJumpLink,
                  })}
                >
                  <Text field={jumpLink.label} />
                </button>
              </li>
            ))}
          </ul>
        </div>
        <div className={styles.dropdown}>
          <select
            className={styles.select}
            value=""
            onChange={(e) =>
              renderedJumpLinks[Number(e.target.value)].element.scrollIntoView({
                behavior: 'smooth',
                block: 'start',
              })
            }
            aria-labelledby="jump-links-dropdown-label"
          >
            <option value="" disabled>
              {header || jumpLinksDropdownLabel}
            </option>
            {renderedJumpLinks.map((jumpLink, index) => (
              <option key={`option-${index}`} value={index}>
                {jumpLink.label}
              </option>
            ))}
          </select>
          <span className={cn(styles['dropdown-label'], 'body-sm')} id="jump-links-dropdown-label">
            {header || jumpLinksDropdownLabel}
            <CaretUp className={styles['dropdown-icon']} />
          </span>
        </div>
      </div>
      <StartIndex />
    </>
  );
};

export default CorePageJumpLinkNavigation;
